import { API_ENDPOINTS } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';

/* RiskLo AI: the AI assistant's API. Chat answers stream as server-sent events. */

async function call(url, { method = 'GET', body } = {}) {
  const res = await fetch(url, {
    method,
    headers: { ...getBearerAuthHeaders(), ...(body ? { 'Content-Type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    const err = new Error(data.error || `Request failed (${res.status})`);
    err.reason = data.reason || null;
    err.status = res.status;
    throw err;
  }
  return data;
}

export const getAiStatus = () => call(API_ENDPOINTS.aiStatus);
export const listAiConversations = () => call(API_ENDPOINTS.aiConversations);
export const getAiConversation = (id) => call(API_ENDPOINTS.aiConversation(id));
export const deleteAiConversation = (id) => call(API_ENDPOINTS.aiConversation(id), { method: 'DELETE' });
export const sendAiFeedback = (messageId, rating, note) => call(API_ENDPOINTS.aiFeedback(messageId), { method: 'POST', body: { rating, note } });
export const getAiAdminSettings = () => call(API_ENDPOINTS.aiAdminSettings);
export const saveAiAdminSettings = (patch) => call(API_ENDPOINTS.aiAdminSettings, { method: 'PUT', body: patch });
export const getAiAdminUsage = () => call(API_ENDPOINTS.aiAdminUsage);
export const getAiProfile = () => call(API_ENDPOINTS.aiProfile);
export const getAiPropFirms = () => call(API_ENDPOINTS.aiAdminPropFirms);
export const aiPropFirmAction = (key, action) => call(API_ENDPOINTS.aiAdminPropFirmAction(key, action), { method: 'POST' });
export const saveAiProfile = (profile) => call(API_ENDPOINTS.aiProfile, { method: 'PUT', body: profile });
export const sendAiGeneralFeedback = ({ kind, rating, message, conversationId, page }) => call(API_ENDPOINTS.aiGeneralFeedback, { method: 'POST', body: { kind, rating, message, conversationId, page } });
export const resolveAiFeedback = (id, resolved = true) => call(API_ENDPOINTS.aiAdminFeedbackResolve(id), { method: 'POST', body: { resolved } });
export const clearAiProfile = () => call(API_ENDPOINTS.aiProfile, { method: 'DELETE' });

/**
 * Split a growing SSE buffer into complete events. Returns { events, rest }.
 * Each event is the parsed JSON of its `data:` lines.
 */
export function parseSseChunk(buffer) {
  const events = [];
  const parts = buffer.split(/\r?\n\r?\n/);
  const rest = parts.pop();
  parts.forEach((block) => {
    const data = block.split(/\r?\n/).filter((l) => l.startsWith('data:')).map((l) => l.slice(5).replace(/^ /, '')).join('\n');
    if (!data) return;
    try { events.push(JSON.parse(data)); } catch (_e) { /* ignore a malformed event */ }
  });
  return { events, rest };
}

/**
 * Send a message and stream the answer. onEvent receives meta / delta / tool /
 * replace / done / error events. Rejects with an Error (with .reason) when the
 * server refuses before streaming (limits, not enabled, not set up).
 */
export async function streamAiChat({ text, conversationId, page, deep = false }, onEvent, signal) {
  const res = await fetch(API_ENDPOINTS.aiChat, {
    method: 'POST',
    headers: { ...getBearerAuthHeaders(), 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({ text, conversationId: conversationId || null, page: page || null, deep: Boolean(deep) }),
    signal,
  });
  const type = res.headers.get('content-type') || '';
  if (!res.ok || !type.includes('text/event-stream')) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.error || `Request failed (${res.status})`);
    err.reason = data.reason || null;
    err.status = res.status;
    throw err;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const { events, rest } = parseSseChunk(buffer);
    buffer = rest;
    events.forEach(onEvent);
  }
  const { events } = parseSseChunk(`${buffer}\n\n`);
  events.forEach(onEvent);
}
